import { Layout, Menu, Tooltip } from 'antd';
import {
  DashboardOutlined,
  DollarOutlined,
  BuildOutlined,
  ToolOutlined,
  FileProtectOutlined,
  TeamOutlined,
  ShopOutlined,
  ApartmentOutlined,
  BookOutlined,
  SettingOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';

const { Sider } = Layout;

interface RailEntry {
  key: string;
  icon: ReactNode;
  labelKey: string;
}

/**
 * Modules listed in the primary rail. Order matches Adam's brief §2 + the
 * cross-cutting areas (Workflows / Settings / About) at the bottom. The
 * primary rail is the user's "I want to switch modules" affordance; the
 * module sidebar to its right shows that module's deep nav.
 */
const ENTRIES: RailEntry[] = [
  { key: '/dashboard', icon: <DashboardOutlined />, labelKey: 'nav.dashboard' },
  { key: '/finance', icon: <DollarOutlined />, labelKey: 'nav.finance' },
  { key: '/engineering', icon: <BuildOutlined />, labelKey: 'nav.engineering' },
  { key: '/operations', icon: <ToolOutlined />, labelKey: 'nav.operations' },
  { key: '/contracts', icon: <FileProtectOutlined />, labelKey: 'nav.contracts' },
  { key: '/hr', icon: <TeamOutlined />, labelKey: 'nav.hr' },
  { key: '/marketing', icon: <ShopOutlined />, labelKey: 'nav.marketing' },
  { key: '/legal', icon: <BookOutlined />, labelKey: 'nav.legal' },
  { key: '/workflows', icon: <ApartmentOutlined />, labelKey: 'nav.workflows' },
  { key: '/settings', icon: <SettingOutlined />, labelKey: 'nav.settings' },
  { key: '/about', icon: <InfoCircleOutlined />, labelKey: 'nav.about' },
];

/**
 * Always-collapsed icon rail. Shows one entry per top-level module; clicking
 * navigates to the module root which triggers `useModuleSidebar` to swap in
 * that module's deep sub-nav. Mirrors the Quantis pattern (icon-only rail
 * plus context-aware sub-sidebar) within the AntD primitives.
 */
export default function PrimaryRail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const activeKey =
    ENTRIES.map((e) => e.key)
      .filter((k) => location.pathname.startsWith(k))
      .sort((a, b) => b.length - a.length)[0] ?? '/dashboard';

  const items = ENTRIES.map((e) => ({
    key: e.key,
    icon: (
      <Tooltip title={t(e.labelKey)} placement="right">
        <span aria-label={t(e.labelKey)}>{e.icon}</span>
      </Tooltip>
    ),
    label: t(e.labelKey),
  }));

  return (
    <Sider collapsed collapsedWidth={64} theme="dark" trigger={null} className="ga-primary-rail">
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[activeKey]}
        onClick={(e) => navigate(e.key)}
        items={items}
        inlineCollapsed
      />
    </Sider>
  );
}
